import { 
  useContext, 
  useEffect, 
  useRef, 
  useState 
} from 'react'

import { 
  AppointmentCountdownAlert, 
  CompactComments, 
  SimpleIconList, 
  UpcomingMeetings, 
  VideoConferenceModal 
} from '@components'

import { NotificationContext, UserContext } from "@context"
import { CREATE_JITSI_CONFERENCE_SUMMARY_MUTATION, CREATE_VIDEO_CONFERENCE_MUTATION } from "@graphql"

import { 
  getConferenceRoomId, 
  getNextAppointments, 
  getSlotStatus, 
  mySqlToLocal 
} from '@helpers'

import { useMutation } from "@apollo/client"

import { 
  Avatar, 
  Button, 
  Modal, 
  ModalContent, 
  ModalFooter, 
  ModalHeader, 
  ModalBody, 
  Radio, 
  RadioGroup, 
  Tab, 
  Tabs 
} from '@heroui/react'

import { Scrollbars } from 'react-custom-scrollbars-2';

import { 
  CalendarDaysIcon, 
  PhoneIcon, 
  Square3Stack3DIcon, 
  UserIcon, 
  VideoCameraSlashIcon 
} from '@heroicons/react/24/outline'

import dayjs from 'dayjs'
import { PropTypes } from 'prop-types'

const isSessionLive = (session) => {
  return dayjs().add(5, "minute").isAfter(mySqlToLocal(session.starts_at)) && dayjs().subtract(5, "minute").isBefore(mySqlToLocal(session.ends_at))
}

const StaffMeetingsModal = ({ 
  event, 
  handleClose, 
  handleUpdateAttendance, 
  handleUpdateTeacherComment, 
  initialSlotId = null, 
  schedule = [],
  session,
}) => {
  const [jitsiToken, setJitsiToken] = useState(null)
  const [isFetchingToken, setIsFetchingToken] = useState(false)
  const [position, setPosition] = useState(0) // change this
  const [showJitsi, setShowJitsi] = useState(false)
  const [upcomingAppointments, setUpcomingAppointments] = useState([])
  const intervalRef = useRef(null)
  const { notify } = useContext(NotificationContext)

  const [createJitsiConferenceSummary] = useMutation(CREATE_JITSI_CONFERENCE_SUMMARY_MUTATION, { fetchPolicy: "no-cache" })

  const [createVideoConference] = useMutation(CREATE_VIDEO_CONFERENCE_MUTATION, {
    onCompleted: (data) => {
      setJitsiToken(data.createVideoConference.token)
      setIsFetchingToken(false)
      setShowJitsi(true)
    },

    onError: (error) => {
      notify({ title: "Error", type: "danger", content: "There was a problem starting the video conference. Please try again.", open: true })
      setIsFetchingToken(false)
    }
  })

  const { user } = useContext(UserContext)

  useEffect(() => {
    if(initialSlotId) {
      const initialPosition = schedule.findIndex((slot) => slot.id === initialSlotId)
      setPosition(initialPosition)
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps  
  
  useEffect(() => {
    const updateUpcomingAppointments = () => {
      const newAppointments = schedule.map((slot) => ({
        id: slot.id,
        attendance_status: slot.attendance_status.toLowerCase(),
        status: getSlotStatus(slot.starts_at, slot.ends_at),
        guardian: slot.parent,
        student: slot.student,
        lesson: slot.lesson,
        starts_at: slot.starts_at,
        ends_at: slot.ends_at,
        subject: slot.subject,
      }));

      setUpcomingAppointments(newAppointments);
    };

    updateUpcomingAppointments(); // Run immediately

    // Calculate time until the next full minute
    const now = new Date();
    const delayUntilNextMinute = (60 - now.getSeconds()) * 1000;

    // Set timeout to start the interval on the next full minute
    const timeout = setTimeout(() => {
      updateUpcomingAppointments(); // Sync update at exact start of next minute

      const interval = setInterval(() => {
        if (isSessionLive(session)) {
          updateUpcomingAppointments();
        }
      }, 60000); // Runs every minute

      intervalRef.current = interval;
    }, delayUntilNextMinute);

     // Cleanup timeout on unmount
    return () => {
      clearTimeout(timeout);

      if (intervalRef.current) {
        clearInterval(intervalRef.current)
      }
    }
  }, [schedule]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleCloseVideoConference = () => {
    setShowJitsi(false)
  }
  
  const handleGoToAppointment = (slotId) => {
    setPosition(schedule.findIndex((slot) => slot.id === slotId))
  }

  const handleLogConferenceSummary = (currentSlot, eventTimes, eventPayloads, noOfParticipants) => {
    if (eventTimes?.joinButtonClicked) {
      createJitsiConferenceSummary({
        variables: {
          input: {
            room_id: getConferenceRoomId(currentSlot.id),
            event_id: event.id,
            booking_slot_id: currentSlot.booking_slot_id,
            join_button_clicked_at: eventTimes?.joinButtonClicked || null,
            joined_at: eventTimes?.conferenceJoined || null,
            left_at: eventTimes?.conferenceEnded || null,
            first_participant_joined_at: eventTimes?.participantJoined || null,
            last_participant_left_at: eventTimes?.participantLeft || null,
            participant_count: noOfParticipants || 0,
            event_payloads: eventPayloads || [],
            is_guest: false,
          }
        },
        
        onError: (error) => console.error("Error creating Jitsi conference summary", error)
      })
    }
  }
  
  const handleNext = () => {
    const newPosition = Math.min(position + 1, schedule.length - 1)

    if (newPosition !== position) {
      setPosition(newPosition)
    }
  }
  
  const handlePrevious = () => {
    const newPosition = Math.max(position - 1, 0)

    if (newPosition !== position) {
      setPosition(newPosition)
    }
  }

  const handleShowVideoConference = (slot) => {
    getJitsiToken(slot)
  }

  const getDetails = () => {
    const details = [
      { icon: <UserIcon />, caption: currentSlot.parent, description: "Parent" },
      { icon: <PhoneIcon />, caption: getPhoneNumbers(currentSlot), description: "Phone" },
      { icon: <Square3Stack3DIcon />, caption: currentSlot.meeting_type, description: "Meeting Type" },
      { icon: <CalendarDaysIcon />, caption: currentSlot.starts_at.format("dddd Do MMMM"), description: "Date" },
    ]

    if (currentSlot.meeting_type_code === "VC") {
      details.push({ icon: <VideoCameraSlashIcon />, caption: event.vc_auto_disconnect ? 'Enabled' : 'Disabled', description: "Auto disconnect call" })
    }
    
    return details
  }
  
  const getJitsiToken = (data) => {
    setIsFetchingToken(true)

    createVideoConference({
      variables: {
        input: {
          slot_id: data.id,
          room_id: getConferenceRoomId(data.id),
        }
      }
    })
  }

  const getPhoneNumbers = (slot) => {
    const misPhone = slot?.parent_mis_phone?.replace(/\s+/g, "") || null
    const sbPhone = slot?.parent_sb_phone?.replace(/\s+/g, "") || null

    if (!misPhone && !sbPhone) return "Unknown"
    if (sbPhone === misPhone || !sbPhone) return misPhone
    if (!misPhone) return sbPhone

    return `${misPhone} / ${sbPhone}`
  }

  const currentSlot = schedule[position] || null
  const roomId = currentSlot ? getConferenceRoomId(currentSlot.id) : null
  const hasVideoConference = currentSlot.meeting_type_code === "VC"
  const nextAppointment = getNextAppointments(schedule, 1);
  const studentPhoto = currentSlot?.student_photo ? `data:image/png;base64,${currentSlot.student_photo}` : null
  const hasAppointmentExpired = currentSlot?.ends_at.isBefore(dayjs())  
  const attendanceQty = currentSlot.attendance_status.toLowerCase() === "attended" ? 1 : currentSlot.attendance_status.toLowerCase() === "absent" ? -1 : 0
  const details = getDetails()

  return (
    <div>
      <Modal
        isOpen={!showJitsi}
        size="5xl"
        scrollBehavior="inside"
        hideCloseButton={true}
        placement="center"
        isDismissable={false}
        isKeyboardDismissDisabled={true}
        radius="lg"
        onClose={() => handleClose()}
        classNames={{
          backdrop: "z-[99] bg-overlay/75",
          base: "max-h-[85vh] bg-content1",
          header: "bg-indigo-800 rounded-t-xl text-white py-3",
          wrapper: "md:items-center z-[100]",
          closeButton: "hover:bg-white/5 text-white mt-2 mr-2",
          footer: "bg-content1 rounded-b-xl",
        }}
      >
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader className="flex items-center justify-between gap-2">
                <div className='flex items-center gap-2'>
                  <CalendarDaysIcon className='w-14 h-14' />
                  
                  <div>
                    <p className='leading-6'>{currentSlot.starts_at.format('H:mm')} - {currentSlot.ends_at.format('H:mm')}</p>
                    <p className='leading-6'>{currentSlot.parent}</p>
                  </div>
                </div>

                {hasVideoConference && (
                  <Button 
                    className="px-12 font-semibold text-white" 
                    onPress={() => handleShowVideoConference(currentSlot)} 
                    color="success" 
                    size="lg"
                    isDisabled={hasAppointmentExpired && user.currentEvent.vc_auto_disconnect}
                    isLoading={isFetchingToken}
                  >
                    Start Call
                  </Button>
                )}
              </ModalHeader>
            
              <ModalBody className="gap-1 px-6 py-3">
                {nextAppointment && <AppointmentCountdownAlert appointment={nextAppointment} handleGoToAppointment={() => handleGoToAppointment(nextAppointment.id)} />}
                
                <div className='flex h-full pt-2 overflow-y-auto gap-7'>
                  <div key={currentSlot.id} className='basis-[44%] animate-fade-in'>
                    <div className="flex items-center gap-3">
                      <Avatar
                        className="w-16 h-16 ml-2 text-large"
                        isBordered
                        src={studentPhoto}
                        showFallback={true}
                      />
                    
                      <div>
                        <p className="font-semibold">{currentSlot.student}</p>
                        <p className='text-content2-foreground'>{currentSlot.lesson}</p>
                        {currentSlot.subject && <p className='text-content2-foreground'>{currentSlot.subject}</p>}
                      </div>
                    </div>

                    <Tabs className="mt-4" aria-label="Options" fullWidth color="secondary" radius="full">
                      <Tab key="general" title="General">
                        <p className="my-2 text-sm text-content2-foreground">Details</p>

                        <SimpleIconList classNames={{ container: "mb-4" }} items={details} />

                        <div>
                          <p className="mb-2 text-sm text-content2-foreground">Attendance</p>
                          
                          <RadioGroup color="secondary" value={attendanceQty} orientation="horizontal" onValueChange={(attendanceQty) => handleUpdateAttendance(attendanceQty, currentSlot)}>
                            <Radio value={-1}>Absent</Radio>
                            <Radio value={1}>Attended</Radio>
                            <Radio value={0}>Unknown</Radio>
                          </RadioGroup>
                        </div>
                      </Tab>

                      <Tab key="notes" title="Notes">
                        <CompactComments
                          id={currentSlot.booking_slot_id}
                          classNames={{ container: "mt-2", input: "min-h-48" }}
                          initialTeacherComment={currentSlot.teacher_comments} 
                          parentComment={currentSlot.parent_comments} 
                          handleUpdateTeacherComment={(comment) => handleUpdateTeacherComment(currentSlot.id, comment)} 
                          parentCommentScrolls={true}
                          teacherCommentScrolls={true}
                        />
                      </Tab>
                    </Tabs>
                  </div>
                      
                  <div className='basis-[54%]'>
                    <Scrollbars style={{ width: "100%", height: 500, overflowX: "hidden" }}>
                      <div className="mr-6">
                        <UpcomingMeetings 
                          items={upcomingAppointments} 
                          handleClick={(id) => handleGoToAppointment(id)} 
                          currentAppointment={currentSlot} 
                        />
                      </div>
                    </Scrollbars>
                  </div>
                </div>
              </ModalBody>
              
              <ModalFooter>
                <div className="flex justify-between w-full">
                  <Button onPress={() => handleClose()} tabIndex={-1}>Close</Button>
                  
                  <div>
                    <Button className="mr-3" onPress={handlePrevious} isDisabled={position-1 < 0}>
                      Previous
                    </Button>
                    
                    <Button onPress={handleNext} isDisabled={position+1 >= schedule.length}>
                      Next
                    </Button>
                  </div>                  
                </div>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
      
      {showJitsi && (
        <VideoConferenceModal 
          bookingDetails={currentSlot}
          event={event}
          handleClose={() => handleCloseVideoConference()} 
          handleUpdateTeacherComment={(comment) => handleUpdateTeacherComment(currentSlot.id, comment)}
          jwtToken={jitsiToken} 
          roomId={roomId}
          vcAutoDisconnect={user.currentEvent.vc_auto_disconnect}
          handleLogConferenceSummary={(eventTimes, events, noOfParticipants) => handleLogConferenceSummary(currentSlot, eventTimes, events, noOfParticipants)}
        />
      )}
    </div>
  )
}

StaffMeetingsModal.propTypes = {
  event: PropTypes.object.isRequired,
  handleClose: PropTypes.func.isRequired,
  handleUpdateAttendance: PropTypes.func.isRequired,
  handleUpdateTeacherComment: PropTypes.func.isRequired,
  initialSlotId: PropTypes.string.isRequired,
  schedule: PropTypes.array.isRequired,
  session: PropTypes.object.isRequired,
}

export default StaffMeetingsModal